'use client';

import type { ButtonBlock } from '@impact/types';
import { cn } from '@/lib/utils';

interface Props {
  block: ButtonBlock;
  editing?: boolean;
}

const sizeClasses = {
  sm: 'text-sm px-5 py-1.5',
  md: 'text-base px-7 py-2.5',
  lg: 'text-lg px-9 py-3.5',
} as const;

/**
 * Pill variant: fully rounded pill button with soft shadow
 */
export function ButtonPill({ block, editing }: Props) {
  const {
    text,
    url,
    style = 'primary',
    size = 'md',
    alignment = 'left',
    fullWidth = false,
    openInNewTab = false,
  } = block.data;

  const styleClasses = {
    primary: 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-md shadow-primary/30',
    secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
    outline: 'border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground',
  } as const;

  const alignClasses = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  } as const;

  return (
    <div className={cn('my-6', alignClasses[alignment])}>
      <a
        href={editing ? '#' : url}
        onClick={editing ? (e) => e.preventDefault() : undefined}
        {...(!editing && openInNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        className={cn(
          'inline-block rounded-full font-medium transition-colors',
          styleClasses[style],
          sizeClasses[size],
          fullWidth && 'block w-full text-center',
        )}
      >
        <span {...(editing ? { 'data-editable': 'data.text' } : {})}>{text}</span>
      </a>
    </div>
  );
}
